import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { LuArrowUpLeft, LuSearch, LuTrendingUp } from "react-icons/lu";
import NoResult from "./Noresult";
import { SearchBoxProps, SearchQuery, Category } from "./types";
import { clearResults, clearSubResults, setSubSearch } from "../../Redux/reducer/Filter";
import { RootState } from "../../Redux/store";
import { titleCase } from "./Navbar";
import { categoryPath } from "../../utils/categoryPath";

/**
 * Search suggestions dropdown — design canvas "10 · Search suggestions".
 * Left: type karte hi suggestions (pehle 3 = poora search, baaki = sub-item);
 * right: matching categories (store se, client-side) + top searches.
 * Data/behaviour wahi purana (/global/searchAssists), sirf layout naya.
 */
const SearchBox = ({ searchResults, isLoading, close, search, onGlassClick, queryData, handleTopSearchesClick }: SearchBoxProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const allCategories = useSelector((state: RootState) => state.values.allCategories as Category[]);

  const q = (search || "").trim().toLowerCase();
  const matchingCats = useMemo(() => {
    const list = Array.isArray(allCategories) ? allCategories : [];
    if (!q) return list.slice(0, 6);
    return list.filter((c) => String(c.name || "").toLowerCase().includes(q)).slice(0, 6);
  }, [allCategories, q]);

  const openCategory = (c: Category) => {
    dispatch(clearResults());
    navigate(categoryPath(c), { state: { id: c.id } });
    close();
  };

  const handleSub = (name: string) => {
    navigate("/subItems");
    dispatch(clearSubResults());
    dispatch(setSubSearch(name));
    close();
  };

  const handleItemClick = (index: number, name: string) => {
    if ([0, 1, 2].includes(index)) onGlassClick();
    else handleSub(name);
  };

  const highlight = (text: string) => {
    if (!q) return <span>{text}</span>;
    const parts = text.split(new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi"));
    return (
      <span>
        {parts.map((part, i) =>
          part.toLowerCase() === q ? (
            <mark key={i} className="rounded-sm bg-[#ffe9a8] px-0.5">{part}</mark>
          ) : (
            <span key={i}>{part}</span>
          )
        )}
      </span>
    );
  };

  const row = "flex w-full items-center gap-3 px-4 py-2.5 text-left text-[14px] hover:bg-ci-bg";
  const head = "px-4 pb-1 pt-3 text-[11px] uppercase tracking-[0.08em] text-ci-muted";

  return (
    <div className="grid w-full grid-cols-1 overflow-hidden rounded-b-md border border-t-0 border-ci-navy bg-white shadow-lg sm:grid-cols-[minmax(0,1fr)_240px]">
      <div className="flex max-h-[420px] flex-col overflow-y-auto py-1">
        {!search ? (
          <>
            <span className={head}><LuTrendingUp size={12} className="mr-1 inline" /> Top searches</span>
            {(queryData || []).slice(0, 8).map((s: SearchQuery) => (
              <button key={s._id} type="button" className={row} onClick={() => handleTopSearchesClick(s.query || "")}>
                <LuSearch size={16} className="shrink-0 text-ci-muted" />
                <span className="truncate">{s.query}</span>
              </button>
            ))}
          </>
        ) : (
          <>
            <button type="button" className={`${row} font-semibold`} onClick={onGlassClick}>
              <LuSearch size={16} className="shrink-0 text-ci-navy" />
              <span className="truncate">
                Search "<span className="text-ci-navy">{search}</span>"
              </span>
            </button>
            {searchResults && searchResults.length ? (
              <>
                <span className={head}>Products &amp; items</span>
                {searchResults.map((r, index) => {
                  const label = index < 3 ? r.itemName || r.name || "" : r.name || r.itemName || "";
                  return (
                    <button key={`${r._id || index}`} type="button" className={row} onClick={() => handleItemClick(index, r.name || "")}>
                      {index < 3 ? (
                        <LuSearch size={16} className="shrink-0 text-ci-muted" />
                      ) : (
                        <LuArrowUpLeft size={16} className="shrink-0 text-ci-muted" />
                      )}
                      <span className="truncate">{highlight(label)}</span>
                    </button>
                  );
                })}
              </>
            ) : isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-t-2 border-ci-navy" />
              </div>
            ) : (
              <NoResult search={search} searchResults={searchResults || []} />
            )}
          </>
        )}
      </div>
      <div className="hidden flex-col border-l border-ci-line bg-ci-bg py-1 sm:flex">
        <span className={head}>Categories</span>
        {matchingCats.length ? (
          matchingCats.map((c) => (
            <button key={c.id} type="button" className={`${row} py-2`} onClick={() => openCategory(c)}>
              <span className="truncate">{titleCase(c.name)}</span>
              <span className="ml-auto text-[12px] text-ci-muted">{c.itemCount}</span>
            </button>
          ))
        ) : (
          <span className="px-4 py-2 text-[13px] text-ci-muted">Koi category match nahi</span>
        )}
        <span className="px-4 pb-3 pt-2 text-[12px] text-ci-muted">
          Size/brand bhi likh sakte hain — "12mm kamdhenu", "2x1 pipe".
        </span>
      </div>
    </div>
  );
};

export default SearchBox;
